import React, { useState } from 'react';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, ScatterChart, Scatter, ErrorBar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

import PanelCard from '../../performance-operations/components/PanelCard';
import { Table } from '../../performance-operations/styles';
import { Hidden, Note } from '../../../components/performance/evaluation/EvaluationParts';
import { formatNum, formatSigned } from '../../../components/performance/evaluation/evaluationAnalytics';
import { NS } from '../config';

// E07 orta alan — tablonun GÖRÜNEN sayfasındaki yöneticiler için iki grafik (sıra ad sırasıdır; grafik bir sıralama üretmez):
//   • Benzer grup puanına karşı ekip puanı: nokta (ekip ortalaması, aralık = ekipteki en düşük–en yüksek) + benzer grup noktası.
//   • Bant dağılımı: yönetici başına yüksek/orta/düşük yüzde yığılmış çubuk (kalibrasyon öncesi puan).
// Küçük ekipte (n < k) değerler sunucuda gizlidir → grafiğe girmez, tabloda "Gizli". Her grafiğin tablo alternatifi vardır.
const nameOf = (r) => r.managerName || '-';

export function PeerComparisonPanel({ panel, scaleMin, scaleMax }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const lang = i18n.language;
  const [table, setTable] = useState(false);
  const rows = panel.data?.data || [];
  const data = rows.filter((r) => r.teamAverage !== null && r.teamAverage !== undefined).map((r) => ({
    name: nameOf(r),
    team: Number(r.teamAverage),
    peer: r.peerAverage === null || r.peerAverage === undefined ? null : Number(r.peerAverage),
    range: [Number(r.teamAverage) - Number(r.teamMin ?? r.teamAverage), Number(r.teamMax ?? r.teamAverage) - Number(r.teamAverage)],
    n: r.teamN,
    peerN: r.peerN,
  }));
  const domain = [Number(scaleMin ?? 1), Number(scaleMax ?? 5)];
  return (
    <PanelCard
      panelKey="peerComparison" title={t(`${NS}.panel.peerComparison`)} span={6}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={data.length === 0}
      emptyText={t(`${NS}.chart.empty`)}
      tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      <Note>{t(`${NS}.chart.pageNote`)}</Note>
      {table ? (
        <Table>
          <thead>
            <tr>
              <th>{t(`${NS}.col.manager`)}</th>
              <th>{t(`${NS}.col.teamAverage`)}</th>
              <th>{t(`${NS}.col.range`)}</th>
              <th>{t(`${NS}.col.peerAverage`)}</th>
              <th>{t(`${NS}.col.deviation`)}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.managerEmployeeId} data-row={r.managerEmployeeId}>
                <td title={nameOf(r)}>{nameOf(r)}</td>
                <td>{r.teamAverage === null || r.teamAverage === undefined ? (r.teamN ? <Hidden ns={NS} /> : '-') : `${formatNum(r.teamAverage, lang)} · ${t(`${NS}.sampleN`, { n: formatNum(r.teamN, lang, 0) })}`}</td>
                <td>{r.teamMin === null || r.teamMin === undefined ? '-' : `${formatNum(r.teamMin, lang)}–${formatNum(r.teamMax, lang)}`}</td>
                <td>{r.peerAverage === null || r.peerAverage === undefined ? '-' : `${formatNum(r.peerAverage, lang)} · ${t(`${NS}.sampleN`, { n: formatNum(r.peerN, lang, 0) })}`}</td>
                <td>{r.deviation === null || r.deviation === undefined ? (r.deviationReason ? t(`${NS}.reason.${r.deviationReason}`) : '-') : formatSigned(r.deviation, lang)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div style={{ width: '100%', height: Math.max(180, data.length * 34 + 60) }}>
          <ResponsiveContainer>
            <ScatterChart margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" dataKey="x" domain={domain} tick={{ fontSize: theme.metrics.font.size.xs }} />
              <YAxis type="category" dataKey="name" allowDuplicatedCategory={false} width={130} tick={{ fontSize: theme.metrics.font.size.xs }} />
              <Tooltip formatter={(value) => formatNum(value, lang)} />
              <Legend wrapperStyle={{ fontSize: theme.metrics.font.size.xs }} />
              <Scatter name={t(`${NS}.col.teamAverage`)} data={data.map((d) => ({ ...d, x: d.team }))} fill={theme.colors.primary.main}>
                <ErrorBar dataKey="range" direction="x" width={6} stroke={theme.colors.primary.main} />
              </Scatter>
              <Scatter name={t(`${NS}.col.peerAverage`)} data={data.filter((d) => d.peer !== null).map((d) => ({ ...d, x: d.peer }))} fill={theme.colors.text.muted} shape="diamond" />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      )}
    </PanelCard>
  );
}

export function BandDistributionPanel({ panel }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const lang = i18n.language;
  const [table, setTable] = useState(false);
  const rows = panel.data?.data || [];
  const pct = (v, n) => (n ? Math.round((1000 * Number(v || 0)) / n) / 10 : 0);
  const data = rows.filter((r) => r.high !== null && r.high !== undefined && r.teamN > 0).map((r) => ({
    name: nameOf(r),
    high: pct(r.high, r.teamN),
    mid: pct(r.mid, r.teamN),
    low: pct(r.low, r.teamN),
  }));
  const series = [
    { key: 'high', color: theme.colors.secondary.main },
    { key: 'mid', color: theme.colors.primary.light },
    { key: 'low', color: theme.colors.auxiliar.danger },
  ];
  return (
    <PanelCard
      panelKey="bandDistribution" title={t(`${NS}.panel.bandDistribution`)} span={6}
      loading={panel.loading} error={panel.error} onRetry={panel.retry} empty={data.length === 0}
      emptyText={t(`${NS}.chart.empty`)}
      tableToggle showTable={table} onToggleTable={() => setTable((v) => !v)}
    >
      <Note>{t(`${NS}.chart.bandNote`)}</Note>
      {table ? (
        <Table>
          <thead>
            <tr>
              <th>{t(`${NS}.col.manager`)}</th>
              {series.map((s) => <th key={s.key}>{t(`${NS}.band.${s.key}`)}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.managerEmployeeId} data-row={r.managerEmployeeId}>
                <td title={nameOf(r)}>{nameOf(r)}</td>
                {series.map((s) => (
                  <td key={s.key}>
                    {r[s.key] === null || r[s.key] === undefined ? (r.teamN ? <Hidden ns={NS} /> : '-')
                      : `${formatNum(r[s.key], lang, 0)} (${formatNum(pct(r[s.key], r.teamN), lang)}%)`}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div style={{ width: '100%', height: Math.max(180, data.length * 30 + 60) }}>
          <ResponsiveContainer>
            <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: theme.metrics.font.size.xs }} unit="%" />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: theme.metrics.font.size.xs }} />
              <Tooltip formatter={(v) => `${formatNum(v, lang)}%`} />
              <Legend wrapperStyle={{ fontSize: theme.metrics.font.size.xs }} />
              {series.map((s) => <Bar key={s.key} dataKey={s.key} name={t(`${NS}.band.${s.key}`)} stackId="band" fill={s.color} />)}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </PanelCard>
  );
}
